// RoleAccordionSection.jsx
// A single role rendered as a full-width collapsible accordion section.
// Open roles expand to show their candidate grid; assigned roles collapse to a summary row.
import React from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Typography,
  Chip,
  Button,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as AssignedIcon,
  RadioButtonUnchecked as OpenIcon,
  PersonAdd as FindIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { COLOR_GOOD, COLOR_WARN } from '../../utils/colors';
import EmployeeNameLink from '../common/EmployeeNameLink';
import { TABS } from '../cards/EmployeeExpandedView';
import CandidatePoolPanel from './CandidatePoolPanel';
import CandidateTile from './CandidateTile';

const STRATEGY_LABELS = {
  balanced: 'Balanced',
  available_now: 'Available Now',
  skills_first: 'Skills First',
  margin_enhancing: 'Margin Enhancing',
};

const STRATEGY_DESCRIPTIONS = {
  balanced: 'Equal weight on skills, availability, team chemistry, and cost.',
  available_now: 'Heavy emphasis on immediate availability. Solid skills still required.',
  skills_first: 'Prioritizes technical match. Minor scheduling gaps acceptable for the right person.',
  margin_enhancing: 'Surfaces cost-efficient candidates. Lower-cost people rank higher when skills are comparable.',
};

const formatDate = (dateString) => {
  if (!dateString || dateString === 'Unknown' || dateString === 'TBD') return 'TBD';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'TBD';
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
  } catch {
    return 'TBD';
  }
};

export default function RoleAccordionSection({
  role,
  rolePool,
  isExpanded,
  onToggle,
  staffingData,
  allTechFitOpen,
  setAllTechFitOpen,
  allEmployees,
  onFindCandidates,
  onViewDetails,
  onAnalyzeChemistry,
  onSelectSearchCandidate,
  onClearSearchCandidate,
  onOpenRoleDetails,
  assignCandidateToRole,
  unassignCandidateFromRole,
  removeAssignment,
  effectiveStrategy,
  roleStrategy,
  globalStrategy,
  onSetRoleStrategy,
}) {
  const isAssigned = role.status === 'assigned';
  const isSearching = rolePool?.isSearching || false;

  const borderColor = isAssigned ? COLOR_GOOD : COLOR_WARN;

  return (
    <Accordion
      expanded={isExpanded}
      onChange={() => onToggle(role.id)}
      disableGutters
      sx={{
        mb: 1,
        border: `2px solid ${borderColor}`,
        borderRadius: '8px !important',
        '&:before': { display: 'none' },
        bgcolor: 'background.paper',
        '&.Mui-expanded': { bgcolor: 'background.paper' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 56,
          px: 2,
          '& .MuiAccordionSummary-content': { alignItems: 'center', my: 0.5, gap: 0 },
        }}
      >
        {/* Left: status icon + role info (flex: 1) */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
          {isAssigned ? (
            <AssignedIcon sx={{ color: COLOR_GOOD, fontSize: '1.1rem', flexShrink: 0, mt: 0.4 }} />
          ) : (
            <OpenIcon sx={{ color: COLOR_WARN, fontSize: '1.1rem', flexShrink: 0, mt: 0.4 }} />
          )}

          {/* Role info block — two rows when assigned */}
          <Box>
            {/* Row 1: role name + metadata */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); onOpenRoleDetails(role); }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.95rem', mr: 0.5 }}>
                  {role.role_name}
                </Typography>
                <InfoIcon sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
              </Box>
              <Chip label={role.level} size="small" sx={{ fontSize: '0.7rem', height: 20, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {role.total_hours ? `${role.total_hours} hrs` : '—'} • {role.hourly_rate ? `$${role.hourly_rate}/hr` : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {formatDate(role.role_start_date)} – {formatDate(role.role_end_date)}
              </Typography>
            </Box>

            {/* Row 2 (assigned only): tech score + employee name + cost rate */}
            {isAssigned && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  label={role.assigned_tech_score ? role.assigned_tech_score.toFixed(1) : 'N/A'}
                  size="small"
                  sx={{
                    width: 34, height: 22, bgcolor: COLOR_GOOD, color: 'black',
                    fontWeight: 'bold', fontSize: '0.7rem',
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: COLOR_GOOD, fontSize: '0.85rem' }}>
                  <EmployeeNameLink
                    employee={{ employee_id: role.assigned_employee_id, name: role.assigned_employee_name }}
                    tab={TABS.ENGAGEMENTS}
                  />
                </Typography>
                {role.assigned_hourly_rate && (
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {role.assigned_hourly_rate}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Center: action button */}
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: 120, px: 2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {isAssigned ? (
            <Button
              size="small"
              onClick={() => removeAssignment(role.id)}
              sx={{ fontSize: '0.7rem', minWidth: 'auto' }}
            >
              Remove
            </Button>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<FindIcon sx={{ fontSize: '0.8rem', color: 'white' }} />}
                onClick={(e) => { e.stopPropagation(); onFindCandidates(role); }}
                disabled={isSearching}
                sx={{
                  fontSize: '0.7rem',
                  bgcolor: '#7b1fa2', color: 'white',
                  '&:hover': { bgcolor: '#6a1b9a', color: 'white' },
                  '&:disabled': { bgcolor: 'rgba(123, 31, 162, 0.5)', color: 'white' },
                }}
              >
                {isSearching ? 'Searching...' : 'Find'}
              </Button>
              {effectiveStrategy && effectiveStrategy !== 'balanced' && (
                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#7b1fa2', fontWeight: 'bold' }}>
                  {STRATEGY_LABELS[effectiveStrategy]}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Right: per-role strategy override */}
        <Box
          sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pr: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title={roleStrategy ? 'This role is using a custom strategy override' : `Using global strategy: ${STRATEGY_LABELS[globalStrategy || 'balanced']}`}>
            <FormControl size="small" sx={{ minWidth: 155 }}>
              <Select
                value={roleStrategy || ''}
                displayEmpty
                onChange={(e) => onSetRoleStrategy(e.target.value || null)}
                sx={{
                  fontSize: '0.7rem',
                  '& .MuiSelect-select': { py: 0.5 },
                  ...(roleStrategy ? { outline: '1px solid #7b1fa2', borderRadius: 1 } : {}),
                }}
                renderValue={(val) => val
                  ? <span style={{ fontWeight: 600 }}>{STRATEGY_LABELS[val]}</span>
                  : <span style={{ color: '#9e9e9e', fontSize: '0.7rem' }}>{STRATEGY_LABELS[globalStrategy || 'balanced']}</span>
                }
              >
                <MenuItem value="">
                  <Box>
                    <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                      Use global: {STRATEGY_LABELS[globalStrategy || 'balanced']}
                    </Typography>
                  </Box>
                </MenuItem>
                {Object.entries(STRATEGY_LABELS).map(([id, label]) => (
                  <MenuItem key={id} value={id}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>{label}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>{STRATEGY_DESCRIPTIONS[id]}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
        {/* Assigned role: show the assigned candidate's tile for reference */}
        {isAssigned && (() => {
          const assignedCandidate =
            rolePool?.candidates?.find(c => c.employee_id === role.assigned_employee_id) ||
            (rolePool?.searchCandidate?.employee_id === role.assigned_employee_id ? rolePool.searchCandidate : null);
          if (!assignedCandidate) return null;
          return (
            <Box sx={{ mb: 2 }}>
              <Typography variant="overline" sx={{ fontSize: '0.6rem', color: 'text.disabled', mb: 1, display: 'block' }}>
                Assigned
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <CandidateTile
                    candidate={assignedCandidate}
                    onViewDetails={onViewDetails}
                    onAnalyzeChemistry={onAnalyzeChemistry ? (c) => onAnalyzeChemistry(c, role) : null}
                    onUnassign={() => unassignCandidateFromRole(role.id)}
                    isAssignedToRole={true}
                    availabilityLabel={assignedCandidate.availability_label}
                    riskLevel={assignedCandidate.risk_level}
                    techFitOpen={allTechFitOpen}
                    onTechFitToggle={() => setAllTechFitOpen(v => !v)}
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })()}

        {/* Divider before swap pool — only when there are other candidates to show */}
        {isAssigned && (rolePool?.candidates?.length > 0 || rolePool?.searchCandidate) && (
          <Divider sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Or choose a different candidate</Typography>
          </Divider>
        )}

        <CandidatePoolPanel
          role={role}
          staffingData={staffingData}
          rolePool={rolePool}
          allTechFitOpen={allTechFitOpen}
          setAllTechFitOpen={setAllTechFitOpen}
          allEmployees={allEmployees}
          onViewDetails={onViewDetails}
          onAnalyzeChemistry={onAnalyzeChemistry}
          onSelectSearchCandidate={onSelectSearchCandidate}
          onClearSearchCandidate={onClearSearchCandidate}
          onFindCandidates={onFindCandidates}
          assignCandidateToRole={assignCandidateToRole}
          unassignCandidateFromRole={unassignCandidateFromRole}
        />
      </AccordionDetails>
    </Accordion>
  );
}

RoleAccordionSection.propTypes = {
  role: PropTypes.object.isRequired,
  rolePool: PropTypes.object,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  staffingData: PropTypes.object,
  allTechFitOpen: PropTypes.bool,
  setAllTechFitOpen: PropTypes.func.isRequired,
  allEmployees: PropTypes.array.isRequired,
  onFindCandidates: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onAnalyzeChemistry: PropTypes.func,
  onSelectSearchCandidate: PropTypes.func.isRequired,
  onClearSearchCandidate: PropTypes.func,
  onOpenRoleDetails: PropTypes.func.isRequired,
  assignCandidateToRole: PropTypes.func.isRequired,
  unassignCandidateFromRole: PropTypes.func.isRequired,
  removeAssignment: PropTypes.func.isRequired,
  effectiveStrategy: PropTypes.string,
  roleStrategy: PropTypes.string,
  globalStrategy: PropTypes.string,
  onSetRoleStrategy: PropTypes.func,
};
